import type { Project, Taak, TaakPrioriteit, TaakSoort, TaakStatus } from '../../gedeeld/types'

/* ===========================================================================
 *  Woorden en datums
 *
 *  Eén plek voor hoe iets heet. In het dashboard heten de kolommen "Te doen,
 *  Bezig, Wacht, Klaar"; hier precies zo, anders praten twee mensen over
 *  dezelfde taak in twee verschillende woorden.
 * =========================================================================== */

export const STATUSSEN: { key: TaakStatus; label: string; kleur: string }[] = [
  { key: 'te_doen', label: 'Te doen', kleur: 'info' },
  { key: 'bezig', label: 'Bezig', kleur: 'brand' },
  { key: 'wacht', label: 'Wacht', kleur: 'oranje' },
  { key: 'klaar', label: 'Klaar', kleur: 'ok' },
]

export const statusLabel = (s: TaakStatus) => STATUSSEN.find((x) => x.key === s)?.label ?? s

export const PRIORITEITEN: { key: TaakPrioriteit; label: string; kleur: string; gewicht: number }[] = [
  { key: 'urgent', label: 'Urgent', kleur: 'danger', gewicht: 4 },
  { key: 'hoog', label: 'Hoog', kleur: 'oranje', gewicht: 3 },
  { key: 'normaal', label: 'Normaal', kleur: 'info', gewicht: 2 },
  { key: 'laag', label: 'Laag', kleur: 'ok', gewicht: 1 },
]

export const prioriteit = (p: TaakPrioriteit) => PRIORITEITEN.find((x) => x.key === p) ?? PRIORITEITEN[2]!

export const SOORTEN: { key: TaakSoort; label: string; kleur: string }[] = [
  { key: 'taak', label: 'Taak', kleur: 'info' },
  { key: 'bug', label: 'Bug', kleur: 'danger' },
  { key: 'verhaal', label: 'Verhaal', kleur: 'ok' },
  { key: 'epic', label: 'Epic', kleur: 'paars' },
  { key: 'subtaak', label: 'Subtaak', kleur: 'info' },
  { key: 'idee', label: 'Idee', kleur: 'brand' },
]

export const soort = (s: TaakSoort) => SOORTEN.find((x) => x.key === s) ?? SOORTEN[0]!

export const KLEUREN = ['brand', 'ok', 'warn', 'danger', 'info', 'paars', 'oranje'] as const

export const PROJECT_STATUS: Record<Project['status'], string> = {
  idee: 'Idee',
  actief: 'Actief',
  gepauzeerd: 'Gepauzeerd',
  afgerond: 'Afgerond',
}

/** WEB-12, of #12 als het project geen sleutel heeft, of niets. */
export function sleutelVan(t: Pick<Taak, 'nummer' | 'projectId'>, projecten: Project[] | Map<string, Project>): string | null {
  if (!t.nummer || !t.projectId) return null
  const p = projecten instanceof Map ? projecten.get(t.projectId) : projecten.find((x) => x.id === t.projectId)
  return p?.sleutel ? `${p.sleutel}-${t.nummer}` : `#${t.nummer}`
}

export function isTeLaat(t: Pick<Taak, 'deadline' | 'status'>): boolean {
  return !!t.deadline && t.status !== 'klaar' && t.deadline < Date.now()
}

/** Op volgorde van wat het eerst moet: te laat, dan prioriteit, dan deadline. */
export function opDringendheid(a: Taak, b: Taak): number {
  const tl = Number(isTeLaat(b)) - Number(isTeLaat(a))
  if (tl) return tl
  const p = prioriteit(b.prioriteit).gewicht - prioriteit(a.prioriteit).gewicht
  if (p) return p
  return (a.deadline ?? Infinity) - (b.deadline ?? Infinity) || a.createdAt - b.createdAt
}

/* ------------------------------------------------------------------ *
 *  Datums
 * ------------------------------------------------------------------ */

const dag = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' })
const dagJaar = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
const volledig = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const tijd = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' })

const ms = (w: number | string) => (typeof w === 'number' ? w : Date.parse(w))

export function datum(w: number | string | null | undefined): string {
  if (w === null || w === undefined || w === '') return ''
  const d = new Date(ms(w))
  return d.getFullYear() === new Date().getFullYear() ? dag.format(d) : dagJaar.format(d)
}

export function datumTijd(w: number | string | null | undefined): string {
  if (w === null || w === undefined || w === '') return ''
  return volledig.format(new Date(ms(w)))
}

export function kloktijd(w: number | string): string {
  return tijd.format(new Date(ms(w)))
}

/** "zojuist", "12 min geleden", "gisteren", of een datum. */
export function geleden(w: number | string | null | undefined): string {
  if (w === null || w === undefined || w === '') return ''
  const t = ms(w)
  const s = Math.round((Date.now() - t) / 1000)
  if (s < 45) return 'zojuist'
  if (s < 3600) return `${Math.round(s / 60)} min geleden`
  if (s < 86400) return `${Math.round(s / 3600)} uur geleden`
  const dagen = Math.floor((startVanDag(Date.now()) - startVanDag(t)) / 86400000)
  if (dagen === 1) return 'gisteren'
  if (dagen < 7) return `${dagen} dagen geleden`
  return datum(t)
}

/** "vandaag", "morgen", "over 3 dagen", "2 dagen te laat". */
export function tegoed(deadline: number | null): string {
  if (!deadline) return ''
  const dagen = Math.round((startVanDag(deadline) - startVanDag(Date.now())) / 86400000)
  if (dagen === 0) return 'vandaag'
  if (dagen === 1) return 'morgen'
  if (dagen === -1) return 'gisteren'
  if (dagen < 0) return `${-dagen} dagen te laat`
  if (dagen < 14) return `over ${dagen} dagen`
  return datum(deadline)
}

export function startVanDag(t: number): number {
  const d = new Date(t)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** Voor <input type="date">: yyyy-mm-dd in de eigen tijdzone. */
export function naarInvoer(t: number | null | undefined): string {
  if (!t) return ''
  const d = new Date(t)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Terug: om 12:00, zoals Werk het doet, zodat een tijdzone hem niet verschuift. */
export function vanInvoer(w: string): number | null {
  if (!w) return null
  return new Date(w + 'T12:00:00').getTime()
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/** De eerste regel van een commitbericht. */
export const eersteRegel = (t: string) => t.split('\n')[0] ?? t

export function initialen(naam: string | null | undefined): string {
  if (!naam) return '?'
  const delen = naam.trim().split(/\s+/).filter(Boolean)
  const eerste = delen[0]?.[0] ?? '?'
  const laatste = delen.length > 1 ? delen[delen.length - 1]?.[0] ?? '' : ''
  return (eerste + laatste).toUpperCase()
}

/** Een vaste kleur per persoon, uit de naam, zodat iemand overal dezelfde is. */
export function persoonKleur(sleutel: string | null | undefined): string {
  const lijst = ['info', 'ok', 'paars', 'oranje', 'brand', 'danger', 'warn']
  let h = 0
  for (const c of sleutel ?? '') h = (h * 31 + c.charCodeAt(0)) >>> 0
  return lijst[h % lijst.length]!
}

export const meervoud = (n: number, een: string, meer: string) => `${n} ${n === 1 ? een : meer}`
